import { Injectable } from '@angular/core';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EEventType } from '@enums/event-tracking.enum';
import { ComponentStore } from '@ngrx/component-store';
import { toEventInvitationSource } from '@utilities/event-payload-mapping.util';
import { catchError, EMPTY, MonoTypeOperatorFunction, Observable, pipe, switchMap, tap } from 'rxjs';
import { AdminInvitationsService } from '../../admin-invitations.service';
import { IAdminUserInvitation, IInvitationCreatePayload, TAdminInvitationRole } from '../../admin-user-group.interface';

interface IAdminInvitationDialogState {
  error: string | null;
  invitation: IAdminUserInvitation | null;
  loading: boolean;
  revoked: boolean;
  sent: boolean;
}

const initialState: IAdminInvitationDialogState = {
  error: null,
  invitation: null,
  loading: false,
  revoked: false,
  sent: false,
};

@Injectable()
export class AdminInvitationDialogStore extends ComponentStore<IAdminInvitationDialogState> {
  public readonly invitation$ = this.selectSignal((state: IAdminInvitationDialogState) => state.invitation);
  public readonly loading$ = this.selectSignal((state: IAdminInvitationDialogState) => state.loading);
  public readonly error$ = this.selectSignal((state: IAdminInvitationDialogState) => state.error);
  public readonly revoked$ = this.selectSignal((state: IAdminInvitationDialogState) => state.revoked);
  public readonly sent$ = this.selectSignal((state: IAdminInvitationDialogState) => state.sent);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          error: errorMessage,
          loading: false,
        });

        return EMPTY;
      }),
    );

  public readonly sendInvitation = this.effect(
    (params$: Observable<{ payload: IInvitationCreatePayload; role: TAdminInvitationRole }>) => {
      return params$.pipe(
        tap(() => this.patchState({ error: null, loading: true, revoked: false, sent: false })),
        switchMap(({ payload, role }) =>
          this.adminInvitationsService.sendInvitation(role, payload).pipe(
            tap((invitation: IAdminUserInvitation) => {
              this.patchState({ invitation, loading: false, sent: true });
              this.eventTrackingService.track(EEventType.userInvitationSent, {
                source: toEventInvitationSource(role),
              });
            }),
            this.handleError('Failed to send invitation'),
          ),
        ),
      );
    },
  );

  public readonly revokeInvitation = this.effect((params$: Observable<{ id: string; role: TAdminInvitationRole }>) => {
    return params$.pipe(
      tap(() => this.patchState({ error: null, loading: true, revoked: false, sent: false })),
      switchMap(({ id, role }) =>
        this.adminInvitationsService.revokeInvitation(role, id).pipe(
          tap((invitation: IAdminUserInvitation) => {
            this.patchState({ invitation, loading: false, revoked: true });
          }),
          this.handleError('Failed to revoke invitation'),
        ),
      ),
    );
  });

  constructor(
    private readonly adminInvitationsService: AdminInvitationsService,
    private readonly eventTrackingService: EventTrackingService,
  ) {
    super(initialState);
  }
}
